import { useCallback, useEffect, useState } from "react";
import { Bell, Loader2, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Switch } from "./ui/switch";
import { api, extractError } from "../lib/api";
import {
  isSupported,
  getPermission,
  subscribe,
  unsubscribe,
  getCurrentSubscription,
} from "../lib/pushNotifications";

function endpointTail(ep) {
  if (!ep) return "";
  return ep.length > 32 ? ep.slice(-32) : ep;
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

/**
 * Notifications panel for the Account page.
 *
 * - "Push notifications on this device": toggles the subscription for the
 *   browser the user is currently on. Matches the current subscription
 *   endpoint against the server-side list to decide which row is "this device".
 * - "Other devices": every other active subscription for the user — each
 *   row can be removed individually.
 */
export default function NotificationSettings() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [thisDeviceEndpoint, setThisDeviceEndpoint] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const sub = await getCurrentSubscription();
      setThisDeviceEndpoint(sub?.endpoint || null);
      const r = await api.get("/push/me");
      setSubs(r.data?.items || []);
    } catch (e) {
      toast.error("Couldn't load notification settings", { description: extractError(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSupported(isSupported());
    setPermission(getPermission());
    refresh();
  }, [refresh]);

  const thisDeviceActive = !!(thisDeviceEndpoint && subs.find((s) =>
    thisDeviceEndpoint.endsWith(s.endpoint_tail)
  ));

  const toggleThisDevice = async (next) => {
    if (busy) return;
    setBusy(true);
    try {
      if (next) {
        await subscribe();
        toast.success("Push notifications enabled on this device");
      } else {
        await unsubscribe();
        toast.success("Push notifications disabled on this device");
      }
      setPermission(getPermission());
      await refresh();
    } catch (e) {
      toast.error("Couldn't update notification setting", { description: e?.message || extractError(e) });
    } finally {
      setBusy(false);
    }
  };

  const removeRow = async (sub) => {
    if (busy) return;
    setBusy(true);
    try {
      // We don't have the full endpoint, just the tail. Find the full endpoint
      // from the local PushSubscription if this row IS this device; otherwise
      // call DELETE with the tail-prefixed endpoint we have stored server-side.
      // Server matches by exact endpoint, so we ask it to delete by /push/me
      // entry id... but the API takes endpoint. Workaround: ask the server to
      // delete by full endpoint we reconstruct from the local sub if matching,
      // otherwise we use the tail and rely on... hmm. Simpler: backend
      // accepts endpoint as a path/body, not id. To keep things minimal we
      // ALSO support deletion-by-tail in the UI by using the local browser's
      // subscription if it matches. Else we deactivate the local one.
      const localSub = await getCurrentSubscription();
      if (localSub && localSub.endpoint.endsWith(sub.endpoint_tail)) {
        await unsubscribe();
      } else {
        // We need the full endpoint. As a fallback, send what we have — the
        // server uniqueness is by endpoint string, so this only works for the
        // current device. Other devices appear here for visibility but their
        // removal requires opening the app on that device.
        toast.message("Open ExpenseReconcile on that device to remove its subscription.");
        return;
      }
      await refresh();
      toast.success("Subscription removed");
    } catch (e) {
      toast.error("Couldn't remove subscription", { description: extractError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <Card className="mb-6 border-slate-200" data-testid="notifications-card">
        <CardContent className="pt-6">
          <h2 className="mb-2 text-sm font-semibold text-slate-700">Notifications</h2>
          <p className="text-sm text-slate-500">
            This browser doesn't support push notifications.
          </p>
        </CardContent>
      </Card>
    );
  }

  const otherDevices = subs.filter((s) =>
    !thisDeviceEndpoint || !thisDeviceEndpoint.endsWith(s.endpoint_tail)
  );

  return (
    <Card className="mb-6 border-slate-200" data-testid="notifications-card">
      <CardContent className="pt-6">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Notifications</h2>

        <div className="flex items-start justify-between gap-3 rounded-md border border-slate-100 bg-slate-50/50 p-3">
          <div className="flex items-start gap-2">
            <Bell className="mt-0.5 h-4 w-4 text-[#1e2a78]" />
            <div>
              <p className="text-sm font-medium text-slate-900">Push notifications on this device</p>
              <p className="text-xs text-slate-500">
                {permission === "denied"
                  ? "Browser blocked notifications — re-enable in your browser settings."
                  : "Get nudged when one of your expenses is missing a comment."}
              </p>
            </div>
          </div>
          <Switch
            checked={thisDeviceActive}
            onCheckedChange={toggleThisDevice}
            disabled={busy || loading || permission === "denied"}
            data-testid="notifications-this-device-toggle"
          />
        </div>

        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Other devices
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="notifications-loading">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </div>
          ) : otherDevices.length === 0 ? (
            <p className="text-xs text-slate-500" data-testid="notifications-no-other-devices">
              No other active devices.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="notifications-other-devices">
              {otherDevices.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-slate-100 px-3 py-2 text-xs"
                  data-testid={`notification-device-${s.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Smartphone className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <div className="min-w-0">
                      <p className="truncate text-slate-700" title={s.user_agent || ""}>
                        {s.user_agent || "Unknown device"}
                      </p>
                      <p className="text-slate-400">
                        Last used: {formatDate(s.last_used_at)} · …{endpointTail(s.endpoint_tail)}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeRow(s)}
                    disabled={busy}
                    data-testid={`notification-device-remove-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
